/**
 * Unit profile panel — spec §F5.7 ("right; draggable; ✕").
 *
 * "Header: crumb `{Country} › {LevelLabel} · {year}` + unit name. Body: Spark of base
 * indicator series (`/indicators/{id}/series?unit_id=`), active-year dot; Target-vs-
 * achievement bar + % (only if targets exist); "Other indicators · year" — 6 kv rows from
 * `/indicators/values/bulk`; evidence note … Footer: `Open in dashboard` …, `Export CSV`
 * …, `Evidence (n)`. Updates live on year/level changes; closes if country filter excludes
 * it."
 *
 * Markup is the mockup's `#infopanel` (`header > .crumb + h3`, `#infobody`, `.mini-chart`,
 * `.kv`, `.note.info`, `.ip-actions`) inside the §F2 `Widget` shell, which is what makes it
 * draggable and clamped like every other floating widget (§F5.11). Two consequences of that
 * choice, logged as NOTES #203: the panel gains the shell's uppercase title bar (the mockup
 * had a bare ✕ floating over the crumb), and it is **not** collapsible — a full-height panel
 * that collapses to a title bar leaves a 3 px sliver of nothing, and the mockup offered no
 * collapse here either.
 *
 * Everything below the header is derived from four §F1-keyed queries, so a year tick, a
 * level radio or a country change re-reads through TanStack Query's cache rather than
 * through any local copy of the numbers.
 */
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Spark } from '../../components/charts/Spark';
import { Widget } from '../../components/Widget';
import {
  exportCsvUrl,
  fetchBulk,
  fetchConfig,
  fetchIndicators,
  fetchSeries,
  modeFor,
  fetchUnitDetail,
  queryKeys,
  type Indicator,
} from '../../lib/api';
import { NO_VALUE, colonSep, fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { adminLevelOf, COUNTRY_NAMES, selectYear, useAppStore, type Country } from '../../lib/state';

/** §F5.7 "6 kv rows". */
export const OTHER_INDICATOR_COUNT = 6;

/** `/indicators` is already ordered by `sort_order` (§D3), so "first 6" is a slice. */
function otherIndicators(
  indicators: Indicator[] | undefined,
  baseId: string | null,
): Indicator[] {
  return (indicators ?? []).filter((i) => i.id !== baseId).slice(0, OTHER_INDICATOR_COUNT);
}

export function UnitProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const unitId = useAppStore((s) => s.selectedUnit);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const setCountry = useAppStore((s) => s.setCountry);
  const indicatorId = useAppStore((s) => s.indicator);
  const reportingLevel = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const yearIdx = useAppStore((s) => s.yearIdx);
  const years = useAppStore((s) => s.years);

  const { data: detail } = useQuery({
    queryKey: queryKeys.unit(unitId ?? ''),
    queryFn: () => fetchUnitDetail(unitId as string),
    enabled: Boolean(unitId),
  });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: Boolean(unitId),
  });

  const base = useMemo(
    () => indicators?.find((i) => i.id === indicatorId) ?? null,
    [indicators, indicatorId],
  );
  const domain = config?.domains.find((d) => d.id === base?.domain) ?? null;

  // §F5.10's Cumulative checkbox: the spark and the big value follow the map's reading.
  // `others` (the `/values/bulk` rows) deliberately do not — that endpoint is annual for
  // every indicator at once (§D3), and mixing the two readings in one table would be worse
  // than one honest one.
  const mode = modeFor(cumulative, base);
  const { data: series } = useQuery({
    queryKey: queryKeys.series(indicatorId ?? '', unitId ?? '', mode),
    queryFn: () => fetchSeries(indicatorId as string, unitId as string, mode),
    enabled: Boolean(unitId && indicatorId),
  });

  const others = useMemo(() => otherIndicators(indicators, indicatorId), [indicators, indicatorId]);
  const otherIds = others.map((i) => i.id);
  // An overlay unit (watershed, partnership) has no `admin_level`; §E4's level query cannot
  // name it, so the rows honestly read "—" rather than borrowing another unit's numbers.
  // `/values/bulk` enumerates *admin* units only (§D3), so the §M3.5 watershed reporting
  // mode falls back to regions here rather than sending a level the endpoint rejects.
  const bulkLevel = detail?.admin_level ?? adminLevelOf(reportingLevel);
  const { data: bulk } = useQuery({
    queryKey: queryKeys.bulk(otherIds, bulkLevel, year, country),
    queryFn: () => fetchBulk(otherIds, bulkLevel, year, country),
    enabled: Boolean(unitId && otherIds.length > 0),
  });

  /**
   * §F5.7 "closes if country filter excludes it" / §F5.11 "country change … closes
   * out-of-scope unit panel". `setCountry` already drops `selectedUnit`, so in the normal
   * flow this never fires — it is the guard for every *other* way the two can disagree: a
   * restored permalink (M3), a unit selected from a search made under a wider scope, a
   * future deep link. Cheap, and the alternative is a panel describing a place the map is
   * no longer showing.
   */
  useEffect(() => {
    if (!unitId || !detail?.country) return;
    if (country !== 'ALL' && detail.country !== country) setSelectedUnit(null);
  }, [unitId, detail?.country, country, setSelectedUnit]);

  if (!unitId) return null;

  // Wave 5: the §D2 payload carries the unit's NASA POWER normals, so the panel needs no
  // query of its own — a year tick or a level change re-reads them through the same cache
  // entry as the crumb (`docs/DATA_SOURCES.md` §4).
  const climate = detail?.climate ?? null;

  const crumb = [detail?.country_name ?? detail?.country, detail?.level_label]
    .filter(Boolean)
    .join(' › ');
  const unitLabel = base?.unit ?? '';
  const seriesYears = series?.years ?? years;
  // The active dot follows the *time bar*, so it is matched by year, not by array index:
  // `/series` returns the programme years and `yearIdx` indexes `years`; equal today, and
  // this survives them diverging.
  const activeIdx = seriesYears.indexOf(years[yearIdx] ?? year);

  const openInDashboard = () => {
    // §F5.7 "navigates, scope=unit country": the dashboard opens on the geography the
    // profile was describing, not on whatever the global filter happened to be.
    const iso = detail?.country;
    if (iso && iso !== country && iso in COUNTRY_NAMES) setCountry(iso as Country);
    void navigate('/dashboard');
  };

  return (
    <Widget
      id="infopanel"
      title={t('profile.title')}
      icon="info"
      collapsible={false}
      onClose={() => setSelectedUnit(null)}
    >
      <header>
        <div className="crumb" data-ipcrumb="">
          {crumb ? `${crumb} · ${year}` : String(year)}
        </div>
        <h3 data-iptitle="">{detail?.name ?? unitId}</h3>
      </header>

      <div id="infobody">
        {base ? (
          <div className="mini-chart">
            <h5>
              {base.name}
              {unitLabel ? ` (${unitLabel})` : ''}
              {colonSep()}
              {t('profile.trend', { from: seriesYears[0], to: seriesYears[seriesYears.length - 1] })}
            </h5>
            <Spark
              series={series?.values ?? []}
              w={300}
              h={70}
              color={domain?.color ?? 'var(--brand)'}
              unit={unitLabel}
              fmt={base.fmt}
              activeIdx={activeIdx}
              labels={seriesYears}
              ariaLabel={t('profile.sparkAria', { indicator: base.name, unit: detail?.name ?? unitId })}
            />
          </div>
        ) : (
          <div className="mini-chart" data-ipnoind="">
            <h5>{t('profile.trendTitle')}</h5>
            <div className="tbl-note" style={{ marginTop: 0 }}>
              {t('profile.noIndicator')}
            </div>
          </div>
        )}

        {/* §F5.7's target-vs-achievement bar is "only if targets exist", and M2 has no
            target table at all (§C has no `indicator_targets` yet) — so the slot stays
            empty rather than being filled with the mockup's `value × 1.18` illustration,
            which would be a made-up number on the panel readers trust for achievement.
            NOTES #204. */}

        {/* `data-ipothers` because this stopped being the panel's only `.sb-title` when
            wave 5 added the climate block below, and two specs matched it by class. */}
        <div className="sb-title" style={{ marginTop: 12 }} data-ipothers="">
          {t('profile.others', { year })}
        </div>
        {others.map((ind) => {
          const v = bulk?.indicators[ind.id]?.[unitId];
          return (
            <div className="kv" key={ind.id} data-ipkv={ind.id}>
              <span>{ind.name}</span>
              <b>
                {typeof v === 'number' ? fmtNum(v, ind.fmt) : NO_VALUE}{' '}
                {typeof v === 'number' && ind.unit ? (
                  <small style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{ind.unit}</small>
                ) : null}
              </b>
            </div>
          );
        })}

        {/* Wave 5's NASA POWER normals (`docs/DATA_SOURCES.md` §4). Two kv rows under their
            own sub-title — deliberately the least invasive shape the feed could take: the
            §F5.7 contract is "six kv rows from /values/bulk", and adding a seventh
            `data-ipkv` row would have made the panel's own promise untrue for a number that
            is not an indicator. `data-ipclim` is therefore a different attribute, not a
            different component. The whole block disappears for a unit POWER does not cover
            (a watershed, a partnership) rather than printing two em dashes. */}
        {climate ? (
          <div data-ipclimate="">
            <div className="sb-title" style={{ marginTop: 12 }}>
              {t('profile.climate', { from: climate.period[0], to: climate.period[1] })}
            </div>
            <div className="kv" data-ipclim="t2m">
              <span>{t('profile.temperature')}</span>
              <b>
                {fmtNum(climate.t2m_c, 1)}{' '}
                <small style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                  °C
                  {climate.t2m_trend_c_decade !== null ? (
                    /* §4 asks for temperature *trends*, and a thirty-year mean has none in
                       it. The slope rides on the same row rather than taking a third one. */
                    <span data-ipclimtrend="">
                      {' '}
                      {t('profile.trendPerDecade', {
                        value: `${climate.t2m_trend_c_decade > 0 ? '+' : ''}${fmtNum(
                          climate.t2m_trend_c_decade,
                          2,
                        )}`,
                      })}
                    </span>
                  ) : null}
                </small>
              </b>
            </div>
            <div className="kv" data-ipclim="solar">
              <span>{t('profile.solar')}</span>
              <b>
                {fmtNum(climate.solar_kwh, 2)}{' '}
                <small style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                  {t('profile.solarUnit')}
                </small>
              </b>
            </div>
            {climate.inherited ? (
              /* A commune shows its region's climate, and a number whose subject is not the
                 unit in the title has to say so on the panel — not only in the payload. */
              <div className="tbl-note" data-ipcliminherited="">
                {t('profile.climateInherited', { unit: climate.unit_name })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="note info" style={{ marginTop: 12 }} data-ipevidence="">
          <Icon name="info" size={13} /> {t('profile.evidenceNote')}
        </div>
      </div>

      <div className="ip-actions">
        <button type="button" className="btn primary sm" data-ipdash="" onClick={openInDashboard}>
          {t('profile.dashboard')}
        </button>
        {/* §D3's unit scope exports the *whole* 2021–2026 series, ignoring `year` — so the
            link no longer sends one, and says what it hands over. Passing the time bar's
            year would have promised a single-year file the endpoint never produces.
            NOTES #213. */}
        <a className="btn ghost sm" data-ipcsv="" href={exportCsvUrl({ unit_id: unitId })} download>
          <Icon name="download" size={13} /> {t('profile.exportCsv')}
        </a>
        <button
          type="button"
          className="btn ghost sm"
          data-ipev=""
          disabled
          title={t('profile.evidenceNote')}
        >
          {t('profile.evidence', { n: 0 })}
        </button>
      </div>
    </Widget>
  );
}

export default UnitProfile;
